import { nextTick, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import * as Sentry from "@sentry/vue";

import { GetProjectSwitcherListDocument, GetProjectSwitcherListQuery, GetProjectSwitcherListQueryVariables } from "@/graphql";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { isStoreResetError } from "@/graphql/services/isStoreResetError";
import { retryAfterStoreReset } from "@/graphql/services/retryAfterStoreReset";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import useHasuraStore from "@/stores/hasura/hasura.store";
import { isActiveProjectStatus } from "./useRecentProjects";

export type SwitcherProject = { id: string; name: string; isActive: boolean };

// Loads the user's project list for the top-nav switcher up front (once on
// creation, plus a refetch any time the active tenant changes) so opening the
// dropdown is instant instead of waiting on a network round trip.
export const useProjectSwitcherProjects = () => {
  const projects = ref<SwitcherProject[]>([]);
  const isLoading = ref(true);

  const authState = useAuthStateStore();
  // hasuraToken is the right signal: it changes only once hasura.store has
  // exchanged the Frontegg access token for a fresh Hasura JWT, so refetching
  // when it updates guarantees we send the new tenant's token (not the old
  // one — user.tenantId updates earlier, before the JWT round-trip completes).
  const { hasuraToken } = storeToRefs(useHasuraStore());

  const fetchProjects = async () => {
    const tenantId = authState.user?.tenantId;
    // Scope to the current org via a server-side tenantId filter rather than
    // post-filtering in JS. A privileged user (accessAllProjects) would
    // otherwise get cross-tenant projects in this switcher.
    if (!tenantId) {
      projects.value = [];
      isLoading.value = false;
      return;
    }
    isLoading.value = true;
    try {
      // Deliberately NOT sending the accessAllProjects header here — the switcher
      // should only show projects the user actually has access to (membership),
      // not every project in the tenant. Letting Hasura apply the default user
      // role permission gives us that filtering for free.
      const result = await retryAfterStoreReset(() =>
        gaiupApolloClient.query<GetProjectSwitcherListQuery, GetProjectSwitcherListQueryVariables>({
          query: GetProjectSwitcherListDocument,
          fetchPolicy: "network-only",
          variables: { tenantId },
        })
      );
      projects.value = (result.data?.project ?? []).map((project) => ({
        id: project.id,
        // The live row name: the current revision's state IS the working state, so it holds no
        // captured project name of its own. Only a FROZEN revision does (RTLCA-5270).
        name: project.name,
        isActive: isActiveProjectStatus(project.projectStatus?.id),
      }));
    } catch (error) {
      // A second reset during the retry; the hasuraToken watcher will fetch again.
      if (isStoreResetError(error)) return;
      Sentry.captureException(error);
      projects.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  // Fetch when the Hasura JWT is known, and again whenever it changes
  // (login, tenant switch, re-auth). nextTick lets hasura.store's watcher
  // push the new token into Apollo's defaultContext before our fetch runs.
  watch(
    hasuraToken,
    async (token) => {
      if (!token) return;
      await nextTick();
      fetchProjects();
    },
    { immediate: true }
  );

  return { projects, isLoading, refetch: fetchProjects };
};
